/**
 * OPENING FRAME PRODUCTS (door realism round, 2026-08-21 — docs/bim-plan/16 §3, Matt's
 * resolved Q1): frame WIDTH and DEPTH are real, coordination-critical dimensions that
 * vary by manufacturer/model — "one of the major causes of problems for us in
 * coordinating walls and finishes." They are curated per TYPE as a `frameSeries`
 * catalog of make/model entries (the sizeSeries pattern exactly: a selector on the
 * type, never type proliferation — no "Casement Loewen" vs "Casement Jeld-Wen" in the
 * type picker), and each INSTANCE picks an entry by name (`frameProduct`, '' = the
 * series' first entry, the sizeSeries first-entry convention).
 *
 * The BUILT-IN defaults below are the exact pre-round constants (previously local to
 * bim-geometry's protos.ts) — a type with no series stored resolves to identical
 * dimensions, so this round is zero-migration for every existing model. protos.ts now
 * imports these constants from here (single source; OPENING_DIMS re-exports them).
 */
import type { CategoryId, FrameProductEntry, ParamValue } from './types'
import { isGateOp } from './categories/openingOps'

/** 2" wood frame/jamb bars. Was 0.17 ft (2.04") until 2026-10-08 — every window's outside
 *  frame then carried a stray 0.08", which made mulled rough openings impossible to land
 *  on a clean dimension (Matt: "the outer frame rounded to the nearest 1/8""). */
export const DEFAULT_FRAME_W = 2 / 12
/** 1" steel narrow-sightline jambs (was 0.08 ft = 0.96" until 2026-10-08) */
export const DEFAULT_FRAME_W_STEEL = 1 / 12

/** FRAME ROUNDING (2026-10-08): a jamb's width snaps to the nearest 1/16", so the two
 *  jambs together are always a whole 1/8" and a window's outside frame (unit width +
 *  2 jambs) lands on 1/8" — whatever a curated frame product or project jamb says. */
export const FRAME_SNAP_FT = 1 / 192
const snapJamb = (ft: number): number => Math.round(ft / FRAME_SNAP_FT) * FRAME_SNAP_FT
/** the pre-round visual frame depth (FRAME_DEPTH) — superseded per instance the
 *  moment a real frame product with its true depth is picked */
export const DEFAULT_FRAME_DEPTH = 0.35

/** the ParamValue-union member (types.ts) under this module's historical name —
 *  make/model + real width (along the wall) + real depth (across it), all FEET
 *  (UI reads/enters inches) */
export type FrameSeriesEntry = FrameProductEntry

/** Normalize a type's raw `frameSeries` value — never throws on malformed/legacy
 *  data (the sizeSeriesEntries convention). */
export function frameSeriesEntries(raw: ParamValue | undefined): FrameSeriesEntry[] {
  if (!Array.isArray(raw)) return []
  return (raw as unknown[]).filter((e): e is FrameSeriesEntry =>
    !!e && typeof e === 'object'
    && typeof (e as { name?: unknown }).name === 'string'
    && typeof (e as { widthFt?: unknown }).widthFt === 'number'
    && typeof (e as { depthFt?: unknown }).depthFt === 'number')
}

export interface ResolvedOpeningFrame {
  name: string
  widthFt: number
  depthFt: number
  /** true when the dims came from the built-in fallback, not a curated entry */
  builtin: boolean
}

/**
 * The effective frame dimensions for one opening, from its MERGED param record
 * (typeParams + instance params — the elevationFigure `P` shape; a ResolvedElement
 * caller passes `{ ...el.typeParams, ...el.params }`). Resolution: the instance's
 * named `frameProduct` in the type's series → the series' FIRST entry → the built-in
 * default for the category/profile (identical to the pre-round constants).
 */
export function resolveOpeningFrame(
  category: CategoryId,
  p: Record<string, ParamValue>,
): ResolvedOpeningFrame {
  // SITE GATES: a gate hangs on the site wall's own masonry — no frame at all, so the
  // rough opening is the gate's own width and every consumer (plan jambs, 3D bars,
  // the wall void) reads zero
  if (category === 'door' && isGateOp(p.operation as string)) {
    return { name: 'None (Wall-Mounted)', widthFt: 0, depthFt: 0, builtin: true }
  }
  // INTERIOR DOOR FRAMES (trimStyle.ts#applyInteriorDoorFrame): the project's interior jamb —
  // stamped only when the door hasn't picked its own frame product
  if (typeof p.__intJambFt === 'number' && typeof p.__intJambDepthFt === 'number') {
    return { name: 'Project interior jamb', widthFt: snapJamb(p.__intJambFt), depthFt: p.__intJambDepthFt, builtin: false }
  }
  const series = frameSeriesEntries(p.frameSeries)
  const pick = typeof p.frameProduct === 'string' ? p.frameProduct.trim() : ''
  if (pick) {
    const hit = series.find((e) => e.name === pick)
    if (hit) return { name: hit.name, widthFt: snapJamb(hit.widthFt), depthFt: hit.depthFt, builtin: false }
  }
  if (series.length) {
    const first = series[0]
    return { name: first.name, widthFt: snapJamb(first.widthFt), depthFt: first.depthFt, builtin: false }
  }
  // PROJECT STYLE phase 2: a steel frame system builds the steel sightline too
  const steel = p.frameSystem === 'steel'
  return {
    name: 'Default',
    widthFt: steel ? DEFAULT_FRAME_W_STEEL : DEFAULT_FRAME_W,
    depthFt: DEFAULT_FRAME_DEPTH,
    builtin: true,
  }
}

/**
 * FLANGE-MOUNT round (Matt, 2026-09-20: "most windows and doors are set to the
 * outside face of a wall (nail flange is used). Then a jamb extender is used
 * to carry the frame through the depth of the wall... Interior door frames
 * tend to get built the full depth of the wall framing"): where a door/
 * window's frame actually sits in the wall's depth — the sizeSeries/
 * frameProduct shadow pattern applied to mounting instead of dimensions.
 * `frameMountDefault` (TYPE) is the firm-standard default for this product;
 * `frameMount` (INSTANCE) overrides it, '' = inherit. 'auto' (the default of
 * the default) derives from the HOST WALL's own Function — an exterior wall's
 * opening flange-mounts to the outside face (jamb extension fills the rest of
 * the depth); anything else gets the traditional full-depth/centered frame.
 * Never affects the type's derived name/identity (typeNaming.ts never reads
 * this) — two instances of the same curated type can resolve differently
 * depending on which wall they land in, or override individually, without
 * ever being treated as different types (Matt: "that's the same problem Revit
 * creates where you have hundreds of door types").
 */
export type FrameMount = 'exterior' | 'centered' | 'interior'

export function resolveFrameMount(
  p: Record<string, ParamValue>,
  hostFunction: ParamValue | undefined,
): FrameMount {
  const isMount = (v: unknown): v is FrameMount => v === 'exterior' || v === 'centered' || v === 'interior'
  const inst = typeof p.frameMount === 'string' ? p.frameMount.trim() : ''
  if (isMount(inst)) return inst
  const typeDefault = typeof p.frameMountDefault === 'string' ? p.frameMountDefault.trim() : ''
  if (isMount(typeDefault)) return typeDefault
  // ZERO MIGRATION: only an EXPLICIT 'auto' derives from the host wall. Every
  // type saved before this round has no `frameMountDefault` stored at all —
  // the schema's own `default: 'centered'` only fills the type EDITOR's field
  // when a type is next opened/saved, it does not retroactively populate
  // existing records — so blank/missing/malformed must fall to 'centered'
  // (the exact pre-round behavior) here too, or every existing door/window on
  // a wall whose type happens to read Function=exterior (the wall schema's
  // own default) would have silently flange-mounted the moment this shipped.
  if (typeDefault === 'auto') return hostFunction === 'exterior' ? 'exterior' : 'centered'
  return 'centered'
}

/**
 * Companion to resolveFrameMount, DOORS ONLY: how a full-depth interior frame
 * meets the drywall — casing (trim board covers the transition) or a kerf
 * jamb (no casing; the jamb itself carries a reveal). Same type-default/
 * instance-override shadow pattern; meaningless once the frame is flange-
 * mounted (there's a jamb extension there instead), so callers only consult
 * this for a 'centered' or 'interior' resolved mount.
 */
export type InteriorFinish = 'casing' | 'kerf'

export function resolveInteriorFinish(p: Record<string, ParamValue>): InteriorFinish {
  const inst = p.interiorFinish
  if (inst === 'casing' || inst === 'kerf') return inst
  const typeDefault = p.interiorFinishDefault
  if (typeDefault === 'casing' || typeDefault === 'kerf') return typeDefault
  return 'casing'
}
